import {
  BookOpen,
  CirclePlus,
  FileText,
  Files,
  KeyRound,
  LayoutDashboard,
  MessagesSquare,
  Send,
  Smartphone,
  Tags,
  Undo2,
  Wallet,
  Webhook,
} from "lucide-react"
import {
  DeleteAccountScreen,
  ProfileScreen,
  SecurityScreen,
  SessionsScreen,
  NotificationsScreen,
  SupportScreen,
} from "@/features/account"
import { ApiKeysScreen } from "@/features/api-keys"
import { DeviceDetailScreen, DevicesScreen } from "@/features/devices"
import { BatchScreen, BulkSendScreen, MessagesScreen, SendScreen } from "@/features/messaging"
import { OverviewScreen } from "@/features/overview"
import { ApiGuideScreen } from "@/features/api-guide"
import { WalletScreen } from "@/features/wallet"
import { PlansScreen } from "@/features/plans"
import { InvoicesScreen } from "@/features/invoices"
import { RefundsScreen } from "@/features/refunds"
import { PayReturnScreen, TopupScreen, TopupStatusScreen } from "@/features/topup"
import { WebhookDetailScreen, WebhooksScreen } from "@/features/webhooks"
import { nav, type RouteDef } from "./route-kit"
import { staffRoutes } from "./staff-routes"

export { NAV_GROUPS, type NavGroup, type RouteDef, type TaskId } from "./route-kit"

/**
 * The route table (LLD §7.2): every signed-in route, with its permission and
 * flags. Slices replace `Planned` with their screens as they land; the table
 * stays the one source the guard, the navigation, the palette and the
 * shortcuts read.
 */
export const featureRoutes: RouteDef[] = [
  {
    path: "/",
    element: OverviewScreen,
    phoneSafe: true,
    owner: "3.3",
    nav: nav(undefined, "overview", LayoutDashboard, 0, "o"),
  },

  // Messaging
  {
    path: "/messages/new",
    element: SendScreen,
    permission: "messaging.sms:send",
    phoneSafe: true,
    owner: "3.1",
    nav: nav("messaging", "send", Send, 1, "s"),
  },
  {
    path: "/messages/bulk",
    element: BulkSendScreen,
    permission: "messaging.sms:send",
    phoneSafe: false,
    owner: "3.1",
    nav: nav("messaging", "bulkSend", Files, 2),
  },
  {
    path: "/messages",
    element: MessagesScreen,
    permission: "messaging.sms:read",
    phoneSafe: true,
    owner: "3.2",
    nav: nav("messaging", "messages", MessagesSquare, 3, "m"),
  },
  {
    path: "/messages/batches/:id",
    element: BatchScreen,
    permission: "messaging.sms:read",
    phoneSafe: true,
    owner: "3.2",
    titleKey: "titles.batch",
  },

  // Gateway
  {
    path: "/devices",
    element: DevicesScreen,
    permission: "devices.device:read",
    phoneSafe: true,
    owner: "2.2",
    nav: nav("gateway", "devices", Smartphone, 1, "d"),
  },
  {
    path: "/devices/:id",
    element: DeviceDetailScreen,
    permission: "devices.device:read",
    phoneSafe: true,
    owner: "2.2",
    titleKey: "titles.device",
  },
  {
    path: "/api-keys",
    element: ApiKeysScreen,
    permission: "apikeys.key:manage",
    phoneSafe: false,
    owner: "2.1",
    nav: nav("gateway", "apiKeys", KeyRound, 2),
  },
  {
    path: "/webhooks",
    element: WebhooksScreen,
    permission: "webhooks.subscription:manage",
    phoneSafe: false,
    owner: "3.5",
    nav: nav("gateway", "webhooks", Webhook, 3),
  },
  {
    path: "/webhooks/:id",
    element: WebhookDetailScreen,
    permission: "webhooks.subscription:manage",
    phoneSafe: false,
    owner: "3.5",
    titleKey: "titles.webhook",
  },
  {
    path: "/api-guide",
    element: ApiGuideScreen,
    phoneSafe: false,
    owner: "3.5",
    nav: nav("gateway", "apiGuide", BookOpen, 4),
  },

  // Billing (normal mode)
  {
    path: "/wallet",
    element: WalletScreen,
    permission: "wallet.balance:read",
    money: true,
    phoneSafe: true,
    owner: "4.1",
    nav: nav("billing", "wallet", Wallet, 1, "w"),
  },
  {
    path: "/wallet/top-up",
    element: TopupScreen,
    permission: "wallet.topup:create",
    money: true,
    phoneSafe: true,
    owner: "4.2",
    nav: nav("billing", "topUp", CirclePlus, 2),
  },
  {
    path: "/wallet/top-up/:id",
    element: TopupStatusScreen,
    permission: "wallet.balance:read",
    money: true,
    phoneSafe: true,
    owner: "4.3",
    titleKey: "titles.topup",
  },
  {
    // Orange Money's return (backend payments returnURL, DW-102).
    path: "/pay/return",
    element: PayReturnScreen,
    permission: "wallet.balance:read",
    money: true,
    phoneSafe: true,
    owner: "4.3",
    titleKey: "titles.topup",
  },
  {
    path: "/plans",
    element: PlansScreen,
    permission: "billing.subscription:read",
    money: true,
    phoneSafe: true,
    owner: "5.1",
    nav: nav("billing", "plans", Tags, 3),
  },
  {
    path: "/invoices",
    element: InvoicesScreen,
    permission: "invoices.document:read",
    money: true,
    phoneSafe: true,
    owner: "5.3",
    nav: nav("billing", "invoices", FileText, 4),
  },
  {
    path: "/refunds",
    element: RefundsScreen,
    permission: ["refunds.request:create", "wallet.balance:read"],
    money: true,
    phoneSafe: true,
    owner: "5.4",
    nav: nav("billing", "refunds", Undo2, 5),
  },

  // Account (the top-bar menu, not the sidebar)
  {
    path: "/account",
    element: ProfileScreen,
    permission: "account.profile:read",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.profile",
  },
  {
    path: "/account/security",
    element: SecurityScreen,
    permission: "account.security:manage",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.security",
  },
  {
    path: "/account/sessions",
    element: SessionsScreen,
    permission: "account.security:manage",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.sessions",
  },
  {
    path: "/account/notifications",
    element: NotificationsScreen,
    permission: "account.profile:read",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.notifications",
  },
  {
    path: "/account/support",
    element: SupportScreen,
    permission: "account.profile:write",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.support",
  },
  {
    path: "/account/delete",
    element: DeleteAccountScreen,
    permission: "account.profile:write",
    staffExempt: true,
    phoneSafe: true,
    owner: "1.5",
    titleKey: "account.delete",
  },

  ...staffRoutes,
]

/** Backend notice e-mails link these (DW-06); each redirects to its route, keeping the query. */
export const routeRedirects: { from: string; to: string; money: boolean }[] = [
  { from: "/wallet/topup", to: "/wallet/top-up", money: true },
  { from: "/billing", to: "/plans", money: true },
]
